import { ResponsiveStatusTabs } from "./ResponsiveStatusTabs";
import { CheckCircleOutline } from "@mui/icons-material";
import {
  Alert, Box, Button, Card, CardContent, Chip, Dialog, DialogActions,
  DialogContent, DialogTitle, FormControl, InputLabel, MenuItem, Select,
  Stack, TextField, Typography, useMediaQuery, useTheme
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  QualityInspection, decidePortalDerogation, loadPortalDerogations
} from "./api";
import { LabeledInfo } from "./LabeledInfo";

export function PortalDerogationInbox({
  canDecide, initialInspectionId = null, onInitialInspectionConsumed
}: {
  canDecide: boolean;
  initialInspectionId?: string | null;
  onInitialInspectionConsumed?: () => void;
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const formatDate = (value: string | null | undefined) => value
    ? new Date(value).toLocaleDateString(i18n.resolvedLanguage) : "—";
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<"open" | "finished">("open");
  const [selected, setSelected] = useState<QualityInspection | null>(null);
  const [decision, setDecision] = useState<"ACCEPTED" | "REJECTED">("ACCEPTED");
  const [extensionDays, setExtensionDays] = useState<number | "">("");
  const [comment, setComment] = useState("");
  const query = useQuery({
    queryKey: ["portal-derogations"], queryFn: loadPortalDerogations
  });
  useEffect(() => {
    if (!initialInspectionId || !query.data) return;
    const target = query.data.find((entry) => entry.id === initialInspectionId);
    if (target) {
      setTab(target.workflowState === "PARTNER_REVIEW" ? "open" : "finished");
      setSelected(target);
    }
    onInitialInspectionConsumed?.();
  }, [initialInspectionId, onInitialInspectionConsumed, query.data]);
  useEffect(() => {
    setDecision("ACCEPTED");
    setExtensionDays(selected?.decidedExtensionDays
      ?? selected?.extensionDays ?? "");
    setComment("");
  }, [selected]);
  const decide = useMutation({
    mutationFn: () => decidePortalDerogation(
      selected!, decision, comment.trim(),
      decision === "ACCEPTED" && extensionDays !== "" ? extensionDays : null
    ),
    onSuccess: async () => {
      setSelected(null);
      await queryClient.invalidateQueries({ queryKey: ["portal-derogations"] });
    }
  });
  const entries = query.data ?? [];
  const openCount = entries.filter((entry) => entry.workflowState === "PARTNER_REVIEW").length;
  const visibleEntries = entries.filter((entry) =>
    (entry.workflowState === "PARTNER_REVIEW") === (tab === "open"));
  return <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: .5 }}>
    <Box sx={{ mb: 1.25 }}>
      <ResponsiveStatusTabs value={tab} onChange={setTab} items={[
        { value: "open", label: t("quality.portalView.open"), mobileLabel: t("quality.portalView.open"), count: openCount },
        { value: "finished", label: t("quality.portalView.finished"), mobileLabel: t("quality.portalView.finished"), count: entries.length - openCount }
      ]} />
    </Box>
    {query.isError && <Alert severity="error">
      {t("quality.portal.loadError")}
    </Alert>}
    {!query.isLoading && !query.isError && entries.length === 0 &&
      <Alert severity="info">{t("quality.portal.empty")}</Alert>}
    {!query.isLoading && !query.isError && entries.length > 0 && visibleEntries.length === 0 && (
      <Alert severity="info">{t(tab === "open" ? "quality.portalView.emptyOpen" : "quality.portalView.emptyFinished")}</Alert>
    )}
    <Stack spacing={1.25}>
      {visibleEntries.map((entry) => <Card key={entry.id} variant="outlined"
        sx={{ borderLeft: "5px solid", borderLeftColor: "primary.main" }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}
            alignItems={{ md: "center" }}>
            <Stack spacing={1.25} sx={{ flex: 1, minWidth: 0 }}>
              <LabeledInfo label={t("quality.derogationCard.reference")} prominent>{entry.inspectionNumber}</LabeledInfo>
              <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
                <LabeledInfo label={t("quality.derogationCard.item")} prominent>{entry.itemCodes}</LabeledInfo>
                <LabeledInfo label={t("quality.derogationCard.hu")}>{entry.huNumber}</LabeledInfo>
                <LabeledInfo label={t("quality.derogationCard.quantity")} prominent>{entry.quantity} {entry.uom}</LabeledInfo>
              </Stack>
              <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}
                sx={{ p: 1.25, bgcolor: "background.default", borderRadius: 1 }}>
                <LabeledInfo label={t("quality.portalView.originalExpiry")} prominent>{formatDate(entry.originalExpirationDate)}</LabeledInfo>
                <LabeledInfo label={t("quality.portalView.extension")} prominent>{entry.extensionDays == null ? "—" : t("quality.portalView.days", { count: entry.extensionDays })}</LabeledInfo>
                <LabeledInfo label={t("quality.portalView.requestedExpiry")} prominent>{formatDate(entry.requestedExpirationDate)}</LabeledInfo>
              </Stack>
              {entry.reportedComment && <LabeledInfo label={t("quality.derogationCard.reason")}>{entry.reportedComment}</LabeledInfo>}
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end">
            <Chip label={entry.workflowState === "PARTNER_REVIEW"
              ? t("quality.portal.toDecide") : t("quality.portal.decided")}
              color={entry.workflowState === "PARTNER_REVIEW" ? "warning" : "default"} />
            <Button variant={entry.workflowState === "PARTNER_REVIEW" && canDecide ? "contained" : "outlined"} sx={{ minHeight: 44 }} onClick={() => { decide.reset(); setSelected(entry); }}>
              {entry.workflowState === "PARTNER_REVIEW" && canDecide
                ? t("quality.actions.decide") : t("quality.actions.view")}
            </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>)}
    </Stack>
    <Dialog open={Boolean(selected)} onClose={() => setSelected(null)}
      fullWidth maxWidth="md" fullScreen={mobile}>
      <DialogTitle sx={{ overflowWrap: "anywhere" }}>{selected && t("quality.portal.dialogTitle", {
        number: selected.inspectionNumber
      })}</DialogTitle>
      <DialogContent dividers>
        {selected && <Stack spacing={2}>
          <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}>
            <LabeledInfo label={t("quality.derogationCard.item")} prominent>{selected.itemCodes}</LabeledInfo>
            <LabeledInfo label={t("quality.derogationCard.hu")}>{selected.huNumber}</LabeledInfo>
            <LabeledInfo label={t("quality.derogationCard.quantity")} prominent>{selected.quantity} {selected.uom}</LabeledInfo>
          </Stack>
          {selected.reportedComment && <Alert severity="info">
            <Typography variant="caption">{t("quality.derogationCard.reason")}</Typography>
            <Typography variant="body2">{selected.reportedComment}</Typography>
          </Alert>}
          <Stack direction="row" useFlexGap flexWrap="wrap" columnGap={3} rowGap={1}
            sx={{ p: 1.25, bgcolor: "background.default", borderRadius: 1 }}>
            <LabeledInfo label={t("quality.portalView.originalExpiry")} prominent>{formatDate(selected.originalExpirationDate)}</LabeledInfo>
            <LabeledInfo label={t("quality.portalView.extension")} prominent>{selected.extensionDays == null ? "—" : t("quality.portalView.days", { count: selected.extensionDays })}</LabeledInfo>
            <LabeledInfo label={t("quality.portalView.requestedExpiry")} prominent>{formatDate(selected.requestedExpirationDate)}</LabeledInfo>
          </Stack>
          {selected.workflowState === "PARTNER_REVIEW" && canDecide ? <>
            <FormControl fullWidth>
              <InputLabel>{t("quality.portal.decision")}</InputLabel>
              <Select value={decision} label={t("quality.portal.decision")}
                onChange={(event) => setDecision(
                  event.target.value as "ACCEPTED" | "REJECTED"
                )}>
                <MenuItem value="ACCEPTED">
                  {t("quality.partnerActions.ACCEPTED")}
                </MenuItem>
                <MenuItem value="REJECTED">
                  {t("quality.partnerActions.REJECTED")}
                </MenuItem>
              </Select>
            </FormControl>
            {decision === "ACCEPTED" && <TextField type="number"
              label={t("quality.labels.approvedExtensionDays")}
              value={extensionDays}
              disabled={!selected.proposalAdjustmentAllowed}
              inputProps={{ min: 1, max: 3650 }}
              onChange={(event) => setExtensionDays(event.target.value === ""
                ? "" : Number(event.target.value))} />}
            <TextField label={t("quality.labels.decisionComment")}
              value={comment} onChange={(event) => setComment(event.target.value)}
              multiline minRows={3} required />
            {decide.isError && <Alert severity="error">
              {decide.error instanceof Error ? decide.error.message
                : t("quality.errors.decision")}
            </Alert>}
          </> : selected.partnerDecision ? <Alert severity={selected.partnerDecision === "REJECTED" ? "error" : "success"}>
            {t("quality.portal.recordedDecision", {
              decision: selected.partnerDecision
                ? t(`quality.partnerActions.${selected.partnerDecision}`) : "—"
            })}
          </Alert> : <Alert severity="info">{t("quality.portalView.readOnly")}</Alert>}
        </Stack>}
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1, "& .MuiButton-root": { minHeight: 44 } }}>
        <Button onClick={() => setSelected(null)}>{t("common.actions.close")}</Button>
        {selected?.workflowState === "PARTNER_REVIEW" && canDecide &&
          <Button variant="contained" startIcon={<CheckCircleOutline />}
            color={decision === "ACCEPTED" ? "success" : "error"}
            disabled={comment.trim().length < 3 || decide.isPending
              || (decision === "ACCEPTED" && (extensionDays === ""
                || extensionDays < 1 || extensionDays > 3650))}
            onClick={() => decide.mutate()}>
            {t("quality.actions.confirmDecision")}
          </Button>}
      </DialogActions>
    </Dialog>
  </Box>;
}
